import { type QueryFunctionContext } from "@tanstack/react-query";
import { lineChartFactory, type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { type ChartPeriod } from "hooks/useChartPeriodMeasure";
import dataService from "../../../../../Application/services/dataServices/typedDataService";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import type { MessagePerson, MessageSession } from "../../types/models";
import { type PaginationParams } from "features/Library/Common/models";

type QueryFilterMessageId = QueryFunctionContext<
  [_: string, filter: PerformanceWithAccountFilter, messageId: number, sendSessionId?: number],
  unknown
>;
type QueryFilterMessageIdPeriod = QueryFunctionContext<
  [
    _: string,
    filter: PerformanceWithAccountFilter,
    messageId: number,
    aggregation: ChartPeriod,
    sendSessionId?: number,
  ],
  unknown
>;

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export const getLineChartQuery = async ({ queryKey, signal }: QueryFilterMessageIdPeriod) => {
  const [, filter, messageId, aggregation, sendSessionId] = queryKey;
  const { data } = await dataService.messages.getMessageLineChart(
    filter,
    messageId,
    aggregation,
    signal,
    sendSessionId,
  );

  return lineChartFactory(data);
};

export const getEngagementQuery = async ({ queryKey, signal }: QueryFilterMessageId) => {
  const [, filter, messageId, sendSessionId] = queryKey;
  const { data } = await dataService.messages.getMessageEngagement(filter, messageId, signal, sendSessionId);

  const usageBarData: DataPoint[] = [
    {
      id: "sends",
      category: "Sends",
      value: data.MessagesSent,
      fillColor: reportingColors.blue,
    },
    {
      id: "clicks",
      category: "Clicks",
      value: data.MessagesClicked,
      fillColor: reportingColors.yellow,
    },
  ];

  const cards = [
    {
      statDescription: "Sends",
      stat: data.MessagesSent,
      popupBody: "The total number of messages sent during the specified time period.",
    },
    {
      statDescription: "Clicks",
      stat: data.MessagesClicked,
      popupBody: "The total number of message clicks during the specified time period.",
    },
    {
      statDescription: "Click-Through Rate",
      stat: data.ClickThroughRate * 100,
      isStatPercentage: true,
      popupBody: "The percentage of messages clicked compared to the total number of messages sent.",
    },
  ];

  return { cards, usageBarData };
};

export const fetchMessagePeople = async (
  filter: PerformanceWithAccountFilter,
  messageId: number,
  pagination: PaginationParams,
  sendSessionId?: number,
  signal?: AbortSignal,
): Promise<FetchPageResult<MessagePerson>> => {
  const response = await dataService.messages.getMessagePeople(filter, messageId, signal, sendSessionId, pagination);

  const responseWithKey = response.data.map((person) => ({ ...person, id: person.UserId }));

  return {
    rows: responseWithKey,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};

export const fetchMessageSessions = async (
  filter: PerformanceWithAccountFilter,
  messageId: number,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<MessageSession>> => {
  const response = await dataService.messages.getMessageSessions(filter, messageId, signal, pagination);

  const responseWithKey = response.data.map((session, idx) => ({ ...session, id: idx }));

  return {
    rows: responseWithKey as MessageSession[],
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};
